import { useRef } from "react";
import { FileArrowUp, FilePdf, Image as ImageIcon, Camera, X } from "@phosphor-icons/react";
import { ACCEPTED_MIME, ACCEPTED_EXTS, isAcceptedFile, fileKind } from "@/lib/api";
import { toast } from "sonner";

const MAX_FILES = 3;

export default function AttachmentPicker({ files, setFiles, compact = false, testIdPrefix = "att" }) {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const remaining = MAX_FILES - (files?.length || 0);
  const atLimit = remaining <= 0;

  const addFiles = (list) => {
    const next = [];
    for (const f of list) {
      if (!isAcceptedFile(f)) {
        toast.error(`Type non accepté: ${f.name}. PDF ou image uniquement.`);
        continue;
      }
      next.push(f);
    }
    if (!next.length) return;
    const total = (files?.length || 0) + next.length;
    if (total > MAX_FILES) {
      toast.error(`Maximum ${MAX_FILES} pièces jointes par demande`);
      const allowed = MAX_FILES - (files?.length || 0);
      if (allowed <= 0) return;
      setFiles([...(files || []), ...next.slice(0, allowed)]);
      return;
    }
    setFiles([...(files || []), ...next]);
  };

  const onFileChange = (e) => {
    addFiles(Array.from(e.target.files || []));
    e.target.value = "";
  };

  const onCameraChange = (e) => {
    addFiles(Array.from(e.target.files || []));
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    addFiles(Array.from(e.dataTransfer.files || []));
  };

  const removeAt = (idx) => {
    const next = [...files];
    next.splice(idx, 1);
    setFiles(next);
  };

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-picker`}>
      <div className="flex items-center justify-between">
        <span className="label-tiny text-zinc-500">Pièces jointes</span>
        <span className="font-mono text-[11px] text-zinc-500" data-testid={`${testIdPrefix}-counter`}>
          {files?.length || 0} / {MAX_FILES}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => !atLimit && fileInputRef.current?.click()}
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          disabled={atLimit}
          className={`border border-dashed border-zinc-300 hover:border-zinc-900 transition-colors flex flex-col items-center justify-center text-center ${compact ? "py-4" : "py-8"} ${atLimit ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
          data-testid={`${testIdPrefix}-add-files`}
        >
          <FileArrowUp size={compact ? 22 : 30} weight="duotone" className="text-zinc-500 mb-1" />
          <div className={`font-medium text-zinc-900 ${compact ? "text-xs" : "text-sm"}`}>Joindre des fichiers</div>
          <div className="text-[11px] text-zinc-500">PDF · PNG · JPG · HEIC</div>
        </button>
        <button
          type="button"
          onClick={() => !atLimit && cameraInputRef.current?.click()}
          disabled={atLimit}
          className={`border border-dashed border-zinc-300 hover:border-zinc-900 transition-colors flex flex-col items-center justify-center text-center bg-zinc-50/50 ${compact ? "py-4" : "py-8"} ${atLimit ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
          data-testid={`${testIdPrefix}-take-photo`}
        >
          <Camera size={compact ? 22 : 30} weight="duotone" className="text-zinc-500 mb-1" />
          <div className={`font-medium text-zinc-900 ${compact ? "text-xs" : "text-sm"}`}>Prendre une photo</div>
          <div className="text-[11px] text-zinc-500">Caméra mobile / webcam</div>
        </button>
      </div>
      {atLimit && (
        <div className="text-[11px] text-orange-700 bg-orange-50 border border-orange-200 px-3 py-1.5">
          Limite de {MAX_FILES} pièces jointes atteinte. Retirez-en une pour en ajouter une autre.
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_MIME}
        multiple
        className="hidden"
        onChange={onFileChange}
        data-testid={`${testIdPrefix}-file-input`}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onCameraChange}
        data-testid={`${testIdPrefix}-camera-input`}
      />

      {files && files.length > 0 && (
        <div className="border border-zinc-200 divide-y divide-zinc-100" data-testid={`${testIdPrefix}-list`}>
          {files.map((f, i) => {
            const kind = fileKind(f);
            const previewUrl = kind === "image" ? URL.createObjectURL(f) : null;
            const key = `${f.name}-${f.size}-${f.lastModified ?? i}`;
            return (
              <div key={key} className="px-3 py-2 flex items-center gap-3">
                {kind === "image" && previewUrl ? (
                  <img src={previewUrl} alt={f.name} className="w-10 h-10 object-cover border border-zinc-200" />
                ) : (
                  <div className="w-10 h-10 bg-red-50 border border-red-200 flex items-center justify-center shrink-0">
                    <FilePdf size={18} weight="duotone" className="text-red-600" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{f.name}</div>
                  <div className="text-[11px] text-zinc-500 font-mono">
                    {kind.toUpperCase()} · {(f.size / 1024).toFixed(1)} Ko
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  className="p-2 hover:bg-zinc-100"
                  data-testid={`${testIdPrefix}-remove-${i}`}
                >
                  <X size={14} weight="bold" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
